import type { WorkItemDto } from '@customTypes/types';
import { WorkItemStatus, WorkItemTypes } from '@customTypes/types';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import dayjs from 'dayjs';

import { notificationStyleConfig } from '@/features/notification/config/notificationConfig';
import {
  useRejectWorkRequestMutation,
  useUpdateWorkInviteMutation,
  useUpdateWorkRequestMutation
} from '@/features/work/api';
import { useRejectWorkInviteMutation } from '@/features/work/api/reject-work-invite';
import { translate } from '@/shared/i18n/translate';
import { useProfileStore } from '@/shared/store/profileStore';

import { interactionWorkItemsStatusFilters } from '../../constants';

export const useManageEmployerView = ({
  workItem
}: {
  workItem: WorkItemDto;
}) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { currentSessionId } = useProfileStore();

  const updateWorkInviteMutation = useUpdateWorkInviteMutation({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            'ClubWorkItemsList',
            workItem.clubProfileId.toString(),
            interactionWorkItemsStatusFilters
          ],
          exact: true
        });
      }
    }
  });

  const updateWorkRequestMutation = useUpdateWorkRequestMutation({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            'ClubWorkItemsList',
            workItem.clubProfileId.toString(),
            interactionWorkItemsStatusFilters
          ],
          exact: true
        });
      }
    }
  });

  const rejectWorkRequestMutation = useRejectWorkRequestMutation();

  const rejectWorkInviteMutation = useRejectWorkInviteMutation();

  const confirmArrival = async () => {
    if (workItem.workItemType === WorkItemTypes.Invite) {
      await updateWorkInviteMutation.mutateAsync({
        params: {
          workInviteId: workItem.workItemId,
          arrivalConfirmed: true,
          status: WorkItemStatus.ContractOngoing,
          startDate: dayjs().toDate().toISOString()
        }
      });
    }

    if (workItem.workItemType === WorkItemTypes.Request) {
      await updateWorkRequestMutation.mutateAsync({
        params: {
          workRequestId: workItem.workItemId,
          arrivalConfirmed: true,
          status: WorkItemStatus.ContractOngoing,
          startDate: dayjs().toDate().toISOString()
        }
      });
    }
  };

  const sendLastWorkDate = async (newEndDate: string) => {
    if (workItem.workItemType === WorkItemTypes.Invite) {
      await updateWorkInviteMutation.mutateAsync(
        {
          params: {
            workInviteId: workItem.workItemId,
            approvedByClub: true,
            status: WorkItemStatus.ClubSetEndDate,
            newEndDate
          }
        },
        {
          onSuccess: () => {
            notifications.show({
              title: translate('notifications.lastWorkDaySeted'),
              ...notificationStyleConfig
            });
          }
        }
      );
    }
    if (workItem.workItemType === WorkItemTypes.Request) {
      await updateWorkRequestMutation.mutateAsync(
        {
          params: {
            workRequestId: workItem.workItemId,
            approvedByClub: true,
            status: WorkItemStatus.ClubSetEndDate,
            newEndDate
          }
        },
        {
          onSuccess: () => {
            notifications.show({
              title: translate('notifications.lastWorkDaySeted'),
              ...notificationStyleConfig
            });
          }
        }
      );
    }
  };

  const rejectWorkItem = async () => {
    if (workItem.workItemType === WorkItemTypes.Request) {
      await rejectWorkRequestMutation.mutateAsync(
        {
          params: {
            workRequestId: workItem.workItemId.toString()
          }
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: [
                'ClubWorkItemsList',
                currentSessionId?.toString(),
                interactionWorkItemsStatusFilters
              ],
              exact: true
            });
            notifications.show({
              title: translate('notifications.workInviteRejected'),
              ...notificationStyleConfig
            });

            router.history.back();
          }
        }
      );
    }
    if (workItem.workItemType === WorkItemTypes.Invite) {
      await rejectWorkInviteMutation.mutateAsync(
        {
          params: {
            workInviteId: workItem.workItemId.toString()
          }
        },
        {
          onSuccess: () => {
            notifications.show({
              title: translate('notifications.workInviteRejected'),
              ...notificationStyleConfig
            });
            queryClient.invalidateQueries({
              queryKey: [
                'ArtistWorkItemsList',
                currentSessionId?.toString(),
                interactionWorkItemsStatusFilters
              ],
              exact: true
            });
            router.history.back();
          }
        }
      );
    }
    queryClient.invalidateQueries({
      queryKey: ['ShowWorkInvite', workItem.workItemId.toString()]
    });
  };

  return {
    functions: { sendLastWorkDate, confirmArrival, rejectWorkItem }
  };
};
